import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

/**
 * Reusable Stock ledger view filtered by item_type buckets.
 * - filterTypes: e.g. ["RAW","PACKING","CONSUMABLE"] for raw, ["FINISHED","SEMI_FINISHED"] for finished
 */
export default function StockLedger({ title, subtitle, filterTypes, ownerLabel, testid, allowMovementTypes = ["IN", "OUT", "TRANSFER"] }) {
  const [balances, setBalances] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [mvOpen, setMvOpen] = useState(false);
  const [mvForm, setMvForm] = useState({ item_id: "", warehouse_id: "", movement_type: allowMovementTypes[0], quantity: 0, batch_number: "", to_warehouse_id: "", unit_cost: 0, notes: "" });
  const [movements, setMovements] = useState([]);

  const matches = (type) => filterTypes.includes(type);

  const load = async () => {
    const [b, i, w, m] = await Promise.all([
      api.get("/inventory/stock"),
      api.get("/inventory/items"),
      api.get("/inventory/warehouses"),
      api.get("/inventory/movements", { params: { limit: 50 } }),
    ]);
    setBalances(b.data.filter((x) => matches(x.item_type)));
    setItems(i.data.filter((x) => matches(x.item_type)));
    setWarehouses(w.data);
    setMovements(m.data.filter((x) => matches(x.item_type)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveMovement = async () => {
    try {
      await api.post("/inventory/movements", { ...mvForm, quantity: Number(mvForm.quantity), unit_cost: Number(mvForm.unit_cost),
        to_warehouse_id: mvForm.movement_type === "TRANSFER" ? mvForm.to_warehouse_id : null });
      toast.success(`${mvForm.movement_type} posted`); setMvOpen(false);
      setMvForm({ item_id: "", warehouse_id: "", movement_type: allowMovementTypes[0], quantity: 0, batch_number: "", to_warehouse_id: "", unit_cost: 0, notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mt-2">Maintained by · {ownerLabel}</div>
        </div>
        <Dialog open={mvOpen} onOpenChange={setMvOpen}>
          <DialogTrigger asChild><Button data-testid={`${testid}-new-mv-btn`}><Plus className="w-4 h-4 mr-1" /> Stock movement</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New stock movement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Item</Label>
                  <Select value={mvForm.item_id} onValueChange={(v) => setMvForm({ ...mvForm, item_id: v })}>
                    <SelectTrigger data-testid="mv-item"><SelectValue placeholder="Pick item" /></SelectTrigger>
                    <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Warehouse</Label>
                  <Select value={mvForm.warehouse_id} onValueChange={(v) => setMvForm({ ...mvForm, warehouse_id: v })}>
                    <SelectTrigger data-testid="mv-wh"><SelectValue placeholder="Pick warehouse" /></SelectTrigger>
                    <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Movement</Label>
                  <Select value={mvForm.movement_type} onValueChange={(v) => setMvForm({ ...mvForm, movement_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{allowMovementTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Quantity</Label><Input type="number" value={mvForm.quantity} onChange={(e) => setMvForm({ ...mvForm, quantity: e.target.value })} /></div>
                <div className="space-y-2"><Label>Unit cost</Label><Input type="number" value={mvForm.unit_cost} onChange={(e) => setMvForm({ ...mvForm, unit_cost: e.target.value })} /></div>
              </div>
              {mvForm.movement_type === "TRANSFER" && (
                <div className="space-y-2"><Label>To warehouse</Label>
                  <Select value={mvForm.to_warehouse_id} onValueChange={(v) => setMvForm({ ...mvForm, to_warehouse_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
                    <SelectContent>{warehouses.filter((w) => w.id !== mvForm.warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2"><Label>Batch (optional)</Label><Input value={mvForm.batch_number} onChange={(e) => setMvForm({ ...mvForm, batch_number: e.target.value })} /></div>
              <Button onClick={saveMovement} data-testid="mv-save">Post movement</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-card p-4 overflow-x-auto">
          <h2 className="font-display font-bold mb-3">Live balances</h2>
          <Table>
            <TableHeader><TableRow><TableHead>SKU · Item</TableHead><TableHead>Warehouse</TableHead><TableHead>Qty</TableHead><TableHead>Avg cost</TableHead></TableRow></TableHeader>
            <TableBody>
              {balances.map((b) => (
                <TableRow key={b.id} data-testid={`stock-row-${b.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{b.item_name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{b.item_sku} · {b.item_type}</div>
                  </TableCell>
                  <TableCell className="text-sm">{b.warehouse_name}</TableCell>
                  <TableCell className="font-mono">
                    {b.quantity}
                    {b.below_reorder && <span className="ml-2 inline-flex items-center text-xs text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />low</span>}
                  </TableCell>
                  <TableCell className="font-mono">₹{Number(b.avg_cost || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {balances.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No stock yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <div className="surface-card p-4 overflow-x-auto">
          <h2 className="font-display font-bold mb-3">Recent movements</h2>
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Item</TableHead><TableHead>Type</TableHead><TableHead>Qty</TableHead></TableRow></TableHeader>
            <TableBody>
              {movements.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs font-mono">{new Date(m.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{m.item_sku}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{m.movement_type}</Badge></TableCell>
                  <TableCell className="font-mono">{m.quantity}</TableCell>
                </TableRow>
              ))}
              {movements.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No movements yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
